import { $moment } from "../funcs"

export interface DateFormatOptions {
    showTime?: boolean
    showRelative?: boolean
    format?: string
}

/**
 * Форматирует ISO дату с учетом часовых поясов
 * @param isoDateString - строка даты в формате ISO
 * @param options - опции форматирования
 * @returns отформатированная строка даты
 */
type DateInput = string | number | Date | null | undefined

function normalizeDateInput(value: DateInput): string | null {
    if (!value) return null
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.toISOString()
    }
    if (typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date.toISOString()
    }
    if (typeof value === 'string') {
        return value
    }
    return null
}

export function formatDate(isoDateString: DateInput, options: DateFormatOptions = {}): string {
    const { showTime = false, showRelative = true, format } = options
    const normalizedValue = normalizeDateInput(isoDateString)
    if (!normalizedValue) return ''
    
    // Добавляем 'Z' только если его нет в строке
    const dateString = normalizedValue.endsWith('Z') ? normalizedValue : normalizedValue + 'Z'
    
    // Парсим дату как UTC и конвертируем в локальное время
    const date = $moment(dateString).local()
    if (!date.isValid()) return ''
    
    if (format) {
        return date.format(format)
    }
    
    if (showRelative) {
        const today = $moment().local().startOf('day')
        const yesterday = $moment().local().subtract(1, 'days').startOf('day')
        
        if (date.isSame(today, 'day')) {
            return showTime ? `Сегодня, ${date.format('HH:mm')}` : 'Сегодня'
        } else if (date.isSame(yesterday, 'day')) {
            return showTime ? `Вчера, ${date.format('HH:mm')}` : 'Вчера'
        }
    }
    
    // Формат по умолчанию
    if (showTime) {
        return date.format('DD.MM.YYYY HH:mm')
    } else {
        return date.format('DD.MM.YYYY')
    }
}

/**
 * Универсальная функция для форматирования даты и времени
 * Используется во всех компонентах для единообразного отображения
 * @param isoDateString - строка даты в формате ISO
 * @returns отформатированная строка даты и времени
 */
export function formatDateTime(isoDateString: DateInput): string {
    const normalizedValue = normalizeDateInput(isoDateString)
    if (!normalizedValue) return ''
    
    // Добавляем 'Z' только если его нет в строке
    const dateString = normalizedValue.endsWith('Z') ? normalizedValue : normalizedValue + 'Z'
    
    // Парсим дату как UTC и конвертируем в локальное время
    const date = $moment(dateString).local()
    if (!date.isValid()) return ''
    const today = $moment().local().startOf('day')
    const yesterday = $moment().local().subtract(1, 'days').startOf('day')
    
    if (date.isSame(today, 'day')) {
        return `Сегодня, ${date.format('HH:mm')}`
    } else if (date.isSame(yesterday, 'day')) {
        return `Вчера, ${date.format('HH:mm')}`
    } else {
        return date.format('LL') + `, ${date.format('HH:mm')}`
    }
}
